import type { FetchKey } from '@app/src/api/fetchHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { Sort } from '@app/src/types/filter'
import type { Unit } from '@app/src/types/resourceExplorer'
import type { FilterCrumb } from './filters/FilterBreadcrumbs'
import type { MRT_ColumnFiltersState, MRT_RowData, MRT_RowSelectionState, MRT_Updater } from 'material-react-table'
import type { Dispatch, ReactNode, SetStateAction } from 'react'

/** Whether rows are all the density's height, or each as tall as its own tallest cell. */
export type MrtRowSizing = 'fixed' | 'content'

/** Which of a table's rows carry a checkbox. */
export enum MrtRowSelectionMode {
  None = 'None',
  // Every row, held as the numeric ids the bulk actions work in.
  AllRows = 'AllRows',
  // Only the rows nested under another, for a table that lists one entity and acts on another beneath
  // it. Selection is then kept as MRT row ids rather than numeric ones, which is what lets a parent and
  // a child with the same id stay apart.
  SubRows = 'SubRows',
}

/** A collection row: an MRT-compatible record with a numeric id (used for selection + getRowId). */
export type MrtRowData = MRT_RowData & { id: number }

// 'custom' hosts a bespoke editor and chip label via renderEditor/renderSummary, for a filter whose
// value doesn't fit the built-in variants (e.g. a compound template + period + level selection).
// 'multi-step' walks through `multiStep.steps` before the values, and can be added more than once.
export type MrtColumnFilterVariant = 'multi-select' | 'range' | 'date-range' | 'custom' | 'multi-step'

/** The grouped dropdown's breadcrumb so far and the column it leads to, for an editor that draws its header. */
export type FilterEditorHeader = { crumbs: FilterCrumb[]; columnLabel: string }

export type MrtFilterOperator = 'is' | 'isNot' | 'empty' | 'notEmpty'

/** The value persisted for a column filter: the chosen operator plus the operator's value (absent for
 * the valueless empty/notEmpty operators). */
export type StoredFilterValue = { operator: MrtFilterOperator; value?: unknown }

/** A selected option in a multi-select filter. The label is persisted alongside the value so chips
 * render the human-readable name immediately on reload, without waiting for a facet fetch. `count` is
 * how many rows the option matches under the other active filters, for display only - never stored. */
export type FilterOption = { value: string; label: string; count?: number }

/** A resolved facet option ({value,label,count}) for a filter dropdown. */
export type FacetOption = { label: string; value: string; count?: number }

/** One choice made on a multi-step filter's step, kept with its label so the chip reads without a lookup. */
export type MultiStepPathEntry = {
  value: string
  label: string
  // A choice the chip leaves out because it is the default reading, like a result's Overall section.
  isOmittedFromChip?: boolean
  // A choice the chip writes straight after the subject rather than as a part of its own: "Baseline 2026".
  isAppendedToSubject?: boolean
  // The heading the step lists this option under. Only for the list; it is not stored with the choice.
  groupLabel?: string
}

/** An endpoint that counts a table's rows, for the values a facet can't count. */
export type MrtCountEndpoint = { endpoint: string; fetchKey: FetchKey }

/** Where a filter counts its values: an endpoint counting rows, and the path from those rows to what the
 * filter is written on ('' when they are the same). */
export type MrtCountTarget = MrtCountEndpoint & { path: string }

export type MrtMultiStepFilterStep = {
  titleIntlId: string
}

/** A multi-step filter's value: the choice made on each step, then the values. */
export type MultiStepFilterValue = {
  path: MultiStepPathEntry[]
  values: FilterOption[]
}

export type MrtMultiStepFilter = {
  steps: MrtMultiStepFilterStep[]
  // Hooks by contract, called only by the open editor, so what the steps and values are built from is fetched
  // only when someone filters on them. The chips read the labels stored in the path instead.
  useStepOptions: (
    stepIndex: number,
    path: MultiStepPathEntry[],
  ) => { options: MultiStepPathEntry[]; isLoading: boolean }
  // Called once the steps are answered, so it can fetch counts for exactly that choice, narrowed by the
  // table's other filters. Counted on countTarget, or on the providers without one.
  useValueOptions: (
    path: MultiStepPathEntry[],
    otherFilterGroups: FilterGroup[],
    countTarget?: MrtCountTarget,
  ) => { options: FilterOption[]; isLoading: boolean }
  // The chip names the column first and reads every choice after it, for steps that qualify one subject
  // rather than name it: "Overall risk · Climate change".
  isChipLedByColumnLabel?: boolean
}

export type MrtColumnFilter = {
  field: string
  variant: MrtColumnFilterVariant
  // matchInSearch: include this enum's translated labels in the global free-text search matching
  // (only for the backend-wired search<Field> enums). Distinct from isEnum, which only tells the
  // facet endpoint to return raw enum values.
  facet?: {
    name: string
    isEnum?: boolean
    matchInSearch?: boolean
    formatLabel?: (rawLabel: string) => string
    // For a facet whose counts aren't the rows the table lists, e.g. one through a collection that keeps history.
    isCountHidden?: boolean
    // Always sent with the facet. A group on the facet's own collection narrows the rows it lists values from.
    filterGroups?: FilterGroup[]
    // Source the facet options from a different resource's facet endpoint than the collection's own
    // (e.g. a column showing data nested from another root resource). Falls back to the config's
    // facetEndpoint/facetFetchKey when omitted.
    endpoint?: string
    fetchKey?: FetchKey
  }
  // A fixed option array, for a filter over a closed enum whose values don't need a facet round-trip.
  // Kept in the order given (risk levels read worst-first, not alphabetically). Takes precedence over
  // `facet`, which would otherwise offer whatever values happen to exist in the data.
  options?: FilterOption[]
  categoryId?: number
  // When set on a `range` filter, inputs accept '200k'/'1.5M' and the chip/editor display this currency.
  currencyUnit?: Unit
  // For the 'custom' variant: the editor body, and the chip's value summary. The value they read and
  // write is the operator's value (unwrapped), the same value handed to toFilterGroups. The editor also
  // receives this column's resolved `facet`/`options` list, and the table's other filters for its own counts.
  renderEditor?: (props: {
    value: unknown
    onChange: (value: unknown) => void
    options: FilterOption[]
    isLoading: boolean
    otherFilterGroups: FilterGroup[]
    header?: FilterEditorHeader
  }) => ReactNode
  // For an editor with steps of its own: the grouped dropdown then hands it `header` instead of drawing the
  // breadcrumb and heading itself, so the editor's steps continue the one breadcrumb.
  isHeaderRenderedByEditor?: boolean
  renderSummary?: (value: unknown) => string
  // What the chip names in place of the column label, for a filter whose value says what it is about:
  // "Spend 2025", as an assessment chip reads "Baseline 2025".
  getChipSubject?: (value: unknown, columnLabel: string) => string
  // Overrides the operators the variant offers by default. Widen it only when toFilterGroups actually
  // answers the extra operators, and narrow it when one of the defaults would mean something the column
  // cannot deliver.
  operators?: MrtFilterOperator[]
  // For the 'multi-step' variant.
  multiStep?: MrtMultiStepFilter
  toFilterGroups: (value: unknown, operator: MrtFilterOperator) => FilterGroup[]
}

/** Where a column comes from, which is what the Add-filter menu and the column picker group it by.
 * Declared in the order the groups appear. */
export enum MrtColumnGroup {
  Provider = 'provider',
  Product = 'product',
  Risk = 'risk',
  Assessment = 'assessment',
}

/** One shared group, bound to where the page's rows reach its entity (see providerGroup, riskGroup). A hook
 * by contract, called on every render, so its columns must keep their identity while nothing changes. */
export type MrtColumnGroupSource<T> = { useColumns: () => MrtColumnConfig<T>[] }

/** A section inside a group in the Add-filter menu. Declared in the order the sections appear. */
export enum MrtColumnSubgroup {
  // The filters a page has of its own inside a shared group, such as a request's status.
  Page = 'page',
  Identity = 'identity',
  CompanyDetails = 'companyDetails',
  ContactDetails = 'contactDetails',
  StatusAndAdministration = 'statusAndAdministration',
  StandardCategories = 'standardCategories',
  CustomCategories = 'customCategories',
  SupplyChain = 'supplyChain',
  Status = 'status',
}

export type MrtColumnConfig<T> = {
  id: string
  group?: MrtColumnGroup
  subgroup?: MrtColumnSubgroup
  headerIntlId?: string
  header?: string
  size?: number
  includes: string[]
  enableSorting?: boolean
  sortField?: string
  renderCell: (row: T) => ReactNode
  // The table's own header and cells for the value this column's filter holds, for a column that shows what
  // its filter asks about: Spend shows the year the filter is on. The filter bar keeps the plain label.
  withFilterValue?: (filterValue: unknown) => Partial<Pick<MrtColumnConfig<T>, 'header' | 'renderCell'>>
  // Draws the selection checkbox in this column, in its header and in its cells, instead of in MRT's
  // own leading checkbox column. For a table that lists one entity and selects another nested under it,
  // where a checkbox at the start of the row reads as selecting the parent. At most one column per
  // table; ignored while the table has no row selection, or while this column is hidden.
  hostsSelectionCheckbox?: boolean
  filter?: MrtColumnFilter
  // The chip for this column is always present (dashed grey border, non-removable). Per page.
  isFilterFixed?: boolean
  // Starts hidden in the column menu, for a column that's worth having but not worth the width by
  // default (secondary identifiers). The user's own choice, once made, always wins. Hidden columns are
  // excluded from the derived include set, so they cost nothing until shown.
  isHiddenByDefault?: boolean
  // Carries a filter without ever being a column: the chip and the Add-filter picker offer it, but the
  // table never renders it and it stays out of the column order, the visibility state and saved views.
  // For a property of a related entity worth filtering on but not worth a column of its own (the
  // supplier behind a product, say). renderCell is never called, so it can just return null.
  isFilterOnly?: boolean
}

/** The current selection, bridged from our numeric-id model to MRT's rowSelection. */
export type MrtSelection<T> = {
  selectedRowsIds: number[]
  setSelectedRowsIds: Dispatch<SetStateAction<number[]>>
  selectedRows: T[]
  rowSelection: MRT_RowSelectionState
  handleRowSelectionChange: (updater: MRT_Updater<MRT_RowSelectionState>) => void
  hasSelection: boolean
  isAllSelected: () => boolean
  handleSelectAll: () => void
  isLoadingAllRows: boolean
  // How many rows "select all" would reach across every page, for the button that offers it. Zero when
  // the table has no cross-page select-all, or before its count is known.
  selectAllCount: number
}

/** The filters a config's select-all has to reach the same rows with: the table's own, chips + search. */
export type MrtSelectAllArgs = { userFilter: FilterGroup[]; searchFilter: FilterGroup[]; enabled: boolean }

/**
 * A SubRows table's cross-page "select all": how many rows it would reach, the rows once fetched,
 * and the fetch itself. Owned by the config because those rows are not the collection's own - the Due
 * diligence tab lists products but selects the supplier relations beneath them.
 */
export type MrtSelectAll<T> = {
  count: number
  rows: T[]
  isFetching: boolean
  fetchRows: () => Promise<T[]>
}

export type MrtRawExportPayload = { filter: FilterGroup[]; sort: Sort; include: string[] }

/** Builds a row's table id. Nested rows get their parent's id, which is what makes a sub-row id unique. */
export type MrtRowIdFactory<T> = (row: T, index: number, parentId?: string) => string

/** Everything an entity's bulk-actions hook needs from the generic scene. Column order/visibility and
 * the column configs are passed so an entity's export can mirror the current table view. */
export type MrtBulkActionsArgs<T> = {
  view: string
  count: number
  pageSize: number
  isFetching: boolean
  userFilter: FilterGroup[]
  searchFilter: FilterGroup[]
  rawExportPayload: MrtRawExportPayload
  selection: MrtSelection<T>
  columnOrder: string[]
  columnVisibility: Record<string, boolean>
  columns: MrtColumnConfig<T>[]
  columnFilters: MRT_ColumnFiltersState
}

/** Ready-to-render toolbar plus any extra portals (drawers, modals) the actions open. `handleExport`
 * is exposed for the page-header export button; `isUpdateDrawerOpen` lets the scene react to the drawer. */
export type MrtBulkActions = {
  toolbar: ReactNode
  extras: ReactNode[]
  handleExport?: (selectedIds?: number[]) => void
  isUpdateDrawerOpen?: boolean
}

/** What derived filters are derived from: the active sort, and the filters the user's own chips already
 * contribute (so nothing is added where the user has chosen it themselves). */
export type MrtDerivedFiltersArgs = { sort: Sort; userFilter: FilterGroup[] }

/** Args for an entity's optional page-header actions hook (export/import/add buttons + their modals). */
export type MrtHeaderActionsArgs = {
  count: number
  isFetching: boolean
  selectedRowsIds: number[]
  handleExport: (selectedIds?: number[]) => void
}

/** Page-header buttons plus any modals they open. */
export type MrtHeaderActions = { actionButtons: ActionButton[]; headerModals: ReactNode }

/**
 * The per-entity configuration that turns the generic MRT collection scene into a concrete page.
 * Bundles the endpoints, the column registry, and the entity-specific hooks so the scene stays a thin
 * orchestration shell (functional-core/imperative-shell + composition over inheritance).
 */
export type MrtCollectionConfig<T extends MrtRowData> = {
  // Namespaces this table's localStorage state. Also opts the table into saved views: list the same
  // key in SAVED_VIEW_PAGES (./mrtPages) to give it the save-view button and a nav-menu entry.
  storageKey: string
  // Namespaces the filters alone, for tables that share them, such as the tabs of one page. Filters
  // without a column in this table are kept but neither shown nor sent. Defaults to storageKey.
  filterStorageKey?: string
  titleIntlId: string
  collectionEndpoint: string
  facetEndpoint: string
  fetchKey: FetchKey
  facetFetchKey: FetchKey
  // The page's own columns. The shared groups it declares in `groups` follow them.
  useColumns: () => MrtColumnConfig<T>[]
  // The shared groups this page offers, each bound to where its rows reach that entity. A page column with
  // the same id as a group column replaces it, to render the page's own cell, and keeps the group's filter
  // unless it has one.
  groups?: Partial<Record<MrtColumnGroup, MrtColumnGroupSource<T>>>
  // The group the Add filter menu opens on. Defaults to the first.
  openingGroup?: MrtColumnGroup
  // The always-present chips, in order. Replaces the columns' isFilterFixed when set.
  defaultChips?: string[]
  getImplicitFilters: (view: string) => FilterGroup[]
  buildSearchFilters: (
    searchTerm: string,
    columns: MrtColumnConfig<T>[],
    facetOptions: Record<string, FacetOption[]>,
  ) => FilterGroup[]
  // Filters the table adds on the user's behalf, because what they set is not specific enough on its
  // own. Spend is the case today: it exists once per period and the table shows one period, so a sort
  // or an interval filter on spend is pinned to that period.
  // The page request only, so facet counts stay on the full result set.
  useDerivedFilters?: (args: MrtDerivedFiltersArgs) => FilterGroup[]
  // Holds the page request back, with the table loading, until a filter the table always needs is set, so
  // it never shows rows outside it. Omitted by tables that can fetch straight away.
  useIsReadyToFetch?: (userFilter: FilterGroup[]) => boolean
  // Omitted by read-only tables, which then also get no selection column (see rowSelectionMode).
  useBulkActions?: (args: MrtBulkActionsArgs<T>) => MrtBulkActions
  useBreadcrumbs?: (view: string, tabs: ResourceCollectionSceneProps['tabs']) => void
  // Optional page-header buttons (export/import/add). Entities without header actions omit this.
  useHeaderActions?: (args: MrtHeaderActionsArgs) => MrtHeaderActions
  // Relations the export should always load, independent of visible columns. Falls back to the visible
  // include when omitted.
  exportIncludes?: string[]
  // Relations the table always loads, independent of visible columns. Use this for a relation whose columns
  // are generated from asynchronously fetched data: without it the include set changes when that data
  // arrives, which changes the request payload and refetches the whole page under a new query key.
  baseIncludes?: string[]
  // Maps the fetched page of items into the MRT row type. Also receives the active column filters as
  // resolved FilterGroups - a filter on a nested collection (e.g. `mappingNodes.actorType.name`) only
  // narrows which root items the backend returns (an `Any()` check), not which entries of that
  // collection come back, so a row model with sub-rows needs this to filter its own children.
  mapItems?: (items: unknown[], userFilter: FilterGroup[]) => T[]
  getRowId?: MrtRowIdFactory<T>
  // Returns a row's children if exists, or undefined if the row is a leaf. MRT uses this to determine whether to render an expand toggle.
  getSubRows?: (row: T) => T[] | undefined
  // Defaults to AllRows when the table has bulk actions and None when it doesn't - a checkbox column
  // with nothing to apply is just noise. Set explicitly to select rows for something other than a bulk
  // action, or to select only the nested ones.
  rowSelectionMode?: MrtRowSelectionMode
  // 'content' lets a row grow to its tallest cell, for cells that are multi-line by design (a name over
  // its details, chips over a caption). The default 'fixed' clips anything past the density height.
  rowSizing?: MrtRowSizing
  // Scrolls the page rather than the rows inside the table. Also drops the sticky column header, and
  // puts the pagination bar and its horizontal scrollbar at the bottom of the page, below the fold.
  scrollWithPage?: boolean
  // Where the built-in expand column is spliced into the initial column order. Only relevant with
  // getSubRows; ignored otherwise.
  expandAfterColumnId?: string
  // Enriches the fetched page with data from a source that isn't (and can't be) reached via the
  // primary collection's `include` mechanism - e.g. a value from an unrelated resource.
  // Omitted when every column's data already arrives via `includes`.
  // Also receives the active column filters: a filter on augmented data can only be applied to a row
  // model's children once those values exist, which is after mapItems has run.
  // `isFetching` holds the whole table in its loading state, so an augmentation whose cells can show
  // their own loading state only sets it while narrowing on data that hasn't arrived yet.
  useRowAugmentation?: (items: T[], userFilter: FilterGroup[]) => { items: T[]; isFetching?: boolean }
  // Cross-page "select all" for a SubRows table, whose selectable rows come from somewhere other
  // than the collection the table pages through. Omitted elsewhere: a flat table's select-all is the
  // collection itself, which the selection already handles.
  useSelectAllRows?: (args: MrtSelectAllArgs) => MrtSelectAll<T>
  defaultExpanded?: boolean
  // A banner above the table plus the filter groups it toggles. For a derived filter with no column of
  // its own (so it can't be a chip in the filter bar): the banner owns both the copy and the on/off
  // state, and calls resetPage when it flips so a stale page can't outlive the narrower result set.
  useBanner?: (args: MrtBannerArgs) => MrtBanner
}

export type MrtBannerArgs = { resetPage: () => void }

export type MrtBanner = { banner: ReactNode; filters: FilterGroup[] }

/** Arguments for the top-content, which renders above the table and below the page tabs. */
export type MrtTopContentArgs = {
  // What narrows the rows below: the implicit, banner, user and search filters.
  filters: FilterGroup[]
  // The same narrowing as the table holds it, so content that links elsewhere can carry the chips
  // rather than re-derive them from the request.
  columnFilters: MRT_ColumnFiltersState
  // Kept separate because they belong to the page request rather than to the result set, the same
  // reason the facet counts leave them out. Content mirroring the page request adds them back.
  derivedFilters: FilterGroup[]
  setColumnFilters: (updater: MRT_Updater<MRT_ColumnFiltersState>) => void
}
